import { isModuleEnabled } from '@dosfilos/domain';
import {
  useState,
  useEffect,
  useRef,
  useCallback,
  lazy,
  Suspense,
  type CSSProperties,
} from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  SchoolRounded,
  MenuBookRounded,
  LoginRounded,
  PersonAddRounded,
  ArrowForwardRounded,
  MenuBookOutlined,
} from '@mui/icons-material';
import { collection, getDocs, query, where, limit, DocumentData } from 'firebase/firestore';
import { db } from '../../lib/firebase';
import type { FeaturedEvent, LandingConfig, TenantConfig, StatItem } from '@dosfilos/domain';
import { todayInZoneWeb } from '@dosfilos/shared';
import { visibleFeaturedEvents } from '../../lib/featured-events';
import { leadSentence } from '../../lib/lead-sentence';
import { FeaturedEvents } from './components/FeaturedEvents';
import { ProgramPath } from './components/ProgramPath';
import { FacultySection } from './components/FacultySection';
import { PillarsSection } from './components/PillarsSection';
import { ContactBlock } from './components/ContactBlock';
import { hasContact, visiblePillars } from '../../lib/landing-content';
import { visibleFaculty } from '../../lib/landing-faculty';
import { fetchPublicCatalog, type PublicCatalog } from '../../lib/api/public-catalog.api';
import {
  buildPublicPath,
  findProgramBySlug,
  programHref,
  programSlug,
} from '../../lib/public-path';
import { LANDING, LANDING_FONT_LINK, colorToRgba, muteColor } from './landing-theme';

// La confesión de fe se lee con el visor de TipTap: se carga solo al abrirla,
// para que el editor no pese en la carga del landing.
const ConfessionDialog = lazy(() => import('./components/ConfessionDialog'));

// ─── Google Fonts ─────────────────────────────────────────────────────────────
const FONT_LINK = LANDING_FONT_LINK;

// ─── Types ────────────────────────────────────────────────────────────────────

interface TenantSnapshot {
  id: string;
  name: string;
  slug: string;
  config?: TenantConfig;
}

interface Program {
  id: string;
  name: string;
  code?: string;
  description?: string;
}

/** Qué partes del landing existen: interruptores de Configuración y secciones con contenido. */
interface LandingVisibility {
  apply: boolean;
  portal: boolean;
  faculty: boolean;
  programs: boolean;
  about: boolean;
  /** Hay docentes visibles: sección «Cuerpo académico» y enlace «Docentes». */
  facultySection: boolean;
}

// ─── Scroll-reveal hook ───────────────────────────────────────────────────────

function useReveal(threshold = 0.1) {
  const ref = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting) {
          setVisible(true);
          obs.disconnect();
        }
      },
      { threshold },
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, [threshold]);
  return { ref, visible };
}

// ─── Responsive hooks ─────────────────────────────────────────────────────────

function useIsMobile() {
  const [is, setIs] = useState(() => window.innerWidth < 640);
  useEffect(() => {
    const fn = () => setIs(window.innerWidth < 640);
    window.addEventListener('resize', fn, { passive: true });
    return () => window.removeEventListener('resize', fn);
  }, []);
  return is;
}

function useIsTablet() {
  const [is, setIs] = useState(() => window.innerWidth < 1024);
  useEffect(() => {
    const fn = () => setIs(window.innerWidth < 1024);
    window.addEventListener('resize', fn, { passive: true });
    return () => window.removeEventListener('resize', fn);
  }, []);
  return is;
}

// ─── Animated counter ─────────────────────────────────────────────────────────

function AnimatedCounter({ target, suffix = '' }: { target: number; suffix?: string }) {
  const [value, setValue] = useState(0);
  const { ref, visible } = useReveal();
  useEffect(() => {
    if (!visible) return;
    const duration = 2000;
    const start = performance.now();
    const tick = (now: number) => {
      const t = Math.min((now - start) / duration, 1);
      const ease = 1 - Math.pow(1 - t, 3);
      setValue(Math.round(ease * target));
      if (t < 1) requestAnimationFrame(tick);
    };
    requestAnimationFrame(tick);
  }, [visible, target]);
  return (
    <span ref={ref}>
      {value}
      {suffix}
    </span>
  );
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

// ─── Not Found ────────────────────────────────────────────────────────────────

function NotFoundPage() {
  return (
    <div
      style={{
        minHeight: '100vh',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        background: '#0d0f0f',
        color: '#6b6b6b',
        fontFamily: 'Inter, sans-serif',
      }}
    >
      <SchoolRounded style={{ fontSize: 64, opacity: 0.25, marginBottom: 24 }} />
      <h2
        style={{
          color: '#d4d0c8',
          margin: '0 0 8px',
          fontSize: 26,
          fontFamily: 'Cormorant Garamond, serif',
        }}
      >
        Seminario no encontrado
      </h2>
      <p style={{ margin: 0, fontSize: 15 }}>El enlace que buscas no existe o fue removido.</p>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// Main Page
// ═══════════════════════════════════════════════════════════════════════════════

export default function SeminaryLandingPage({ slugOverride }: { slugOverride?: string } = {}) {
  // On a custom landing domain the page renders at "/" (no :slug route param),
  // so the resolved slug is passed in via slugOverride. On the base host it's
  // the /s/:slug route param.
  const params = useParams<{ slug: string }>();
  const slug = slugOverride ?? params.slug;
  const navigate = useNavigate();
  const location = useLocation();

  const [tenant, setTenant] = useState<TenantSnapshot | null>(null);
  const [programs, setPrograms] = useState<Program[]>([]);
  // Catálogo público (ruta de formación + malla): si falla, el landing queda
  // como siempre, con la lista que sale de la lectura directa de programas.
  const [catalog, setCatalog] = useState<PublicCatalog | null>(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  // Al volver desde la página de un programa se llega a `…#programas`: el navegador
  // no puede saltar al ancla porque la sección aparece recién cuando cargan los
  // datos. Se salta cuando existe.
  const hash = location.hash.slice(1);
  useEffect(() => {
    if (!hash || loading) return;
    const el = document.getElementById(hash);
    if (el) el.scrollIntoView({ block: 'start' });
  }, [hash, loading, catalog, programs]);

  // Navbar scroll
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 60);
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  // Data fetch
  useEffect(() => {
    if (!slug) {
      setNotFound(true);
      setLoading(false);
      return;
    }
    let cancelled = false;

    (async () => {
      try {
        const snap = await getDocs(
          query(collection(db, 'tenants'), where('slug', '==', slug), limit(1)),
        );
        if (snap.empty) {
          if (!cancelled) {
            setNotFound(true);
            setLoading(false);
          }
          return;
        }

        const [tenantDoc] = snap.docs;
        if (!tenantDoc) {
          if (!cancelled) {
            setNotFound(true);
            setLoading(false);
          }
          return;
        }

        const data = tenantDoc.data() as DocumentData;
        const cfg = data['config'] as TenantConfig | undefined;
        const resolved: TenantSnapshot = {
          id: tenantDoc.id,
          name: data['name'] as string,
          slug: data['slug'] as string,
          ...(cfg !== undefined ? { config: cfg } : {}),
        };

        if (cancelled) return;
        setTenant(resolved);
        setLoading(false);

        // Los programas salen SOLO del catálogo público (`getPublicCatalog`), que
        // publica una proyección. El documento del programa trae datos internos
        // (revisores, aranceles, rúbricas) y deja de ser legible sin sesión.
        // Si el catálogo falla, el landing se queda sin sección de programas.
        const countsPrograms = (resolved.config?.landing?.stats ?? []).some(
          (st) => st.source === 'activePrograms',
        );
        if (resolved.config?.landing?.showPrograms !== false || countsPrograms) {
          fetchPublicCatalog(resolved.slug)
            .then((c) => {
              if (cancelled) return;
              setCatalog(c);
              setPrograms(
                c.programs
                  .map((p) => ({
                    id: p.id,
                    name: p.name,
                    code: p.code,
                    ...(p.description ? { description: p.description } : {}),
                  }))
                  .sort((x, y) => x.name.localeCompare(y.name)),
              );
            })
            .catch(() => {
              /* sin catálogo: sin sección de programas */
            });
        }
      } catch (err) {
        console.error('[SeminaryLandingPage] Tenant resolution failed:', err);
        if (!cancelled) {
          setNotFound(true);
          setLoading(false);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [slug]);

  const color = tenant?.config?.primaryColor ?? LANDING.DEFAULT_COLOR; // default muted gold
  const muted = muteColor(color);
  const landing: Partial<LandingConfig> = tenant?.config?.landing ?? {};
  const logoUrl = tenant?.config?.logoUrl;

  const handleApply = useCallback(() => navigate(`/s/${slug}/apply`), [navigate, slug]);
  // The student portal lives on the APP/backoffice domain, same as the staff
  // dashboard. Send students straight there so they authenticate ONCE on the
  // right origin (logging in on this landing domain would bounce to the app
  // domain via DomainRoleEnforcer and force a second login — Firebase Auth
  // sessions are per-origin).
  const handlePortal = useCallback(() => {
    const backoffice = tenant?.config?.customDomains?.backoffice;
    if (backoffice) {
      window.location.href = `https://${backoffice}/portal-v2`;
      return;
    }
    navigate('/portal-v2');
  }, [navigate, tenant]);
  const handleFaculty = useCallback(() => {
    // Staff dashboards live on the seminary's APP/backoffice custom domain. If one
    // is configured, send faculty straight there so they authenticate ONCE on the
    // right origin (same rationale as handlePortal).
    const backoffice = tenant?.config?.customDomains?.backoffice;
    if (backoffice) {
      window.location.href = `https://${backoffice}/login`;
      return;
    }
    navigate('/login');
  }, [navigate, tenant]);

  // ── Loading ───────────────────────────────────────────────────────────────
  if (loading) {
    return (
      <div
        style={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          background: '#0d0f0f',
        }}
      >
        <div
          style={{
            width: 36,
            height: 36,
            borderRadius: '50%',
            border: '2px solid rgba(176,141,87,0.15)',
            borderTopColor: '#b08d57',
            animation: 'spin 1s linear infinite',
          }}
        />
        <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>
      </div>
    );
  }

  if (notFound || !tenant) return <NotFoundPage />;

  // ── Design tokens ─────────────────────────────────────────────────────────
  const { BG, BG2, BORDER, TEXT_PRIMARY, TEXT_MUTED, TEXT_SECONDARY, SERIF, SANS } = LANDING;
  const GOLD = muted;
  const GOLD_ALPHA = (a: number) => colorToRgba(color, a);

  // «Hoy» es el día del seminario, no el del navegador.
  const today = todayInZoneWeb(tenant.config?.timezone || 'America/Santiago');
  const featuredEvents = visibleFeaturedEvents(landing.featuredEvents, today);

  // La ruta de formación y el enlace de cada programa a su página pública.
  const path = catalog && landing.showPrograms !== false ? buildPublicPath(catalog) : null;
  const basePath = slugOverride ? '' : `/s/${slug}`;
  const inPath = new Set(path?.rungs.map((r) => r.program.id) ?? []);
  const listedPrograms = programs.filter((p) => !inPath.has(p.id));
  const programHrefFor = (p: Program): string | undefined => {
    const pub = catalog ? findProgramBySlug(catalog, p.id) : undefined;
    return pub && catalog ? programHref(basePath, programSlug(pub, catalog.programs)) : undefined;
  };

  const faculty = visibleFaculty(landing.faculty);
  const pillars = visiblePillars(landing.pillars);

  // Los interruptores de Configuración → Landing. Sin valor = encendido, como siempre.
  const mission = tenant.config?.mission;
  const show: LandingVisibility = {
    // Con Admisiones apagado (Configuración → Módulos) no se ofrece postular.
    apply: landing.ctaAdmissions !== false && isModuleEnabled(tenant.config?.modules, 'admissions'),
    portal: landing.ctaPortal !== false,
    faculty: landing.ctaFaculty !== false,
    programs: landing.showPrograms !== false && (programs.length > 0 || !!path),
    about: !!(mission?.text?.trim() || landing.about?.trim()),
    facultySection: faculty.length > 0,
  };

  return (
    <>
      <style>{`
                ${FONT_LINK}
                *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }
                html { scroll-behavior: smooth; }
                body { background: ${BG}; font-family: ${SANS}; color: ${TEXT_PRIMARY}; overflow-x: hidden; }

                /* Reveal */
                .reveal { opacity: 0; transform: translateY(28px); transition: opacity 0.75s ease, transform 0.75s ease; }
                .reveal.visible { opacity: 1; transform: translateY(0); }
                .reveal-delay-1 { transition-delay: 0.1s; }
                .reveal-delay-2 { transition-delay: 0.22s; }
                .reveal-delay-3 { transition-delay: 0.34s; }
                .reveal-delay-4 { transition-delay: 0.46s; }

                @keyframes fadeUp {
                    from { opacity: 0; transform: translateY(20px); }
                    to   { opacity: 1; transform: none; }
                }
                @keyframes spin { to { transform: rotate(360deg); } }
                @media (prefers-reduced-motion: reduce) {
                    [style*="fadeUp"] { animation: none !important; }
                    html { scroll-behavior: auto; }
                }

                /* ── Navbar ──────────────────────────────────────────────────── */
                .sl-nav {
                    position: fixed; top: 0; left: 0; right: 0; z-index: 100;
                    transition: background 0.35s ease, box-shadow 0.35s ease, border-color 0.35s ease;
                    padding: 0 48px; height: 64px; display: flex; align-items: center;
                    border-bottom: 1px solid transparent;
                }
                .sl-nav.scrolled {
                    background: rgba(13,15,15,0.96);
                    backdrop-filter: blur(12px);
                    -webkit-backdrop-filter: blur(12px);
                    border-bottom-color: ${BORDER};
                    box-shadow: 0 1px 32px rgba(0,0,0,0.4);
                }
                .nav-desktop { display: flex; align-items: center; gap: 32px; }
                .nav-hamburger { display: none; background: none; border: none; cursor: pointer; padding: 8px; color: ${TEXT_PRIMARY}; }
                .nav-mobile-menu {
                    display: none;
                    position: fixed; top: 64px; left: 0; right: 0; z-index: 99;
                    background: rgba(13,15,15,0.98);
                    backdrop-filter: blur(16px);
                    -webkit-backdrop-filter: blur(16px);
                    border-bottom: 1px solid ${BORDER};
                    flex-direction: column;
                    padding: 16px 24px 24px;
                    gap: 16px;
                }
                .nav-mobile-menu.open { display: flex; }

                /* Hover link */
                .nav-link {
                    color: ${TEXT_SECONDARY}; font-size: 13px; font-weight: 500;
                    letter-spacing: 0.06em; text-transform: uppercase; text-decoration: none;
                    transition: color 0.2s;
                }
                .nav-link:hover { color: ${TEXT_PRIMARY}; }

                /* ── Grids ───────────────────────────────────────────────────── */
                .prog-grid { display: grid; grid-template-columns: repeat(auto-fill, minmax(280px, 1fr)); gap: 1px; }
                .prog-item {
                    padding: 36px 32px;
                    border-bottom: 1px solid ${BORDER};
                    transition: background 0.25s ease; cursor: default;
                }
                .prog-item:hover { background: rgba(255,255,255,0.03); }
                .pillar-grid { display: grid; grid-template-columns: repeat(auto-fill, minmax(260px, 1fr)); gap: 32px; }
                .stat-grid { display: grid; grid-template-columns: repeat(4, 1fr); gap: 0; }

                /* ── CTA buttons ─────────────────────────────────────────────── */
                .btn-cta {
                    display: inline-flex; align-items: center; gap: 10px;
                    background: ${GOLD}; color: #0d0f0f;
                    padding: 14px 32px; border-radius: 2px;
                    font-family: ${SANS}; font-size: 13px; font-weight: 600;
                    letter-spacing: 0.08em; text-transform: uppercase;
                    border: none; cursor: pointer;
                    transition: filter 0.2s ease, transform 0.2s ease;
                    white-space: nowrap;
                }
                .btn-cta:hover { filter: brightness(1.1); transform: translateY(-1px); }
                .btn-ghost {
                    display: inline-flex; align-items: center; gap: 10px;
                    background: transparent; color: ${TEXT_PRIMARY};
                    padding: 14px 32px; border-radius: 2px;
                    font-family: ${SANS}; font-size: 13px; font-weight: 500;
                    letter-spacing: 0.08em; text-transform: uppercase;
                    border: 1px solid ${BORDER}; cursor: pointer;
                    transition: border-color 0.2s ease, background 0.2s ease, transform 0.2s ease;
                    white-space: nowrap;
                }
                .btn-ghost:hover { border-color: rgba(255,255,255,0.18); background: rgba(255,255,255,0.04); transform: translateY(-1px); }

                /* full-width buttons on mobile */
                .btn-cta.full-mobile, .btn-ghost.full-mobile { width: 100%; justify-content: center; }

                /* ── Decorative ──────────────────────────────────────────────── */
                .vertical-label {
                    writing-mode: vertical-rl; text-orientation: mixed;
                    transform: rotate(180deg); font-size: 10px; font-weight: 600;
                    letter-spacing: 0.18em; text-transform: uppercase; color: ${TEXT_MUTED};
                }
                .gold-underline {
                    display: inline-block; border-bottom: 1.5px solid ${GOLD}; padding-bottom: 2px;
                }
                .section-label {
                    font-size: 11px; font-weight: 600; letter-spacing: 0.2em;
                    text-transform: uppercase; color: ${GOLD}; margin-bottom: 16px;
                }

                /* El título se reparte en líneas parejas: nunca una palabra suelta abajo. */
                .hero-title { text-wrap: balance; }

                /* ── Tablet (< 1024px) ───────────────────────────────────────── */
                @media (max-width: 1023px) {
                    .sl-nav { padding: 0 32px; }
                    .stat-grid { grid-template-columns: repeat(2, 1fr); }
                    .nav-desktop { display: none; }
                    .nav-hamburger { display: flex; }
                }

                /* ── Mobile (< 640px) ────────────────────────────────────────── */
                @media (max-width: 639px) {
                    .sl-nav { padding: 0 20px; }
                    .hero-title { font-size: clamp(2.2rem, 9vw, 3.5rem) !important; }
                    .pillar-grid { grid-template-columns: 1fr; gap: 20px; }
                    .prog-grid { grid-template-columns: 1fr; }
                    .stat-grid { grid-template-columns: repeat(2, 1fr); }
                    .btn-cta, .btn-ghost { padding: 13px 20px; font-size: 12px; }
                }
            `}</style>

      {/* ── NAVBAR ─────────────────────────────────────────────────────── */}
      <NavBar
        tenant={tenant}
        logoUrl={logoUrl}
        GOLD={GOLD}
        SERIF={SERIF}
        SANS={SANS}
        TEXT_PRIMARY={TEXT_PRIMARY}
        BORDER={BORDER}
        scrolled={scrolled}
        show={show}
        onApply={handleApply}
        onPortal={handlePortal}
      />

      {/* ── HERO ───────────────────────────────────────────────────────── */}
      <HeroSection
        tenant={tenant}
        landing={landing}
        logoUrl={logoUrl}
        GOLD={GOLD}
        SERIF={SERIF}
        SANS={SANS}
        BG={BG}
        TEXT_PRIMARY={TEXT_PRIMARY}
        TEXT_SECONDARY={TEXT_SECONDARY}
        BORDER={BORDER}
        GOLD_ALPHA={GOLD_ALPHA}
        featuredEvents={featuredEvents}
        today={today}
        show={show}
        onApply={handleApply}
        onPortal={handlePortal}
        onFaculty={handleFaculty}
      />

      {/* ── MISSION STATEMENT ──────────────────────────────────────────── */}
      {show.about && (
        <MissionSection
          landing={landing}
          config={tenant.config}
          BG2={BG2}
          SERIF={SERIF}
          SANS={SANS}
          TEXT_PRIMARY={TEXT_PRIMARY}
          TEXT_SECONDARY={TEXT_SECONDARY}
          GOLD={GOLD}
          BORDER={BORDER}
        />
      )}

      {/* ── STATS ────────────────────────────────────────────────── */}
      {landing.showStats !== false && landing.stats && landing.stats.length > 0 && (
        <StatsSection
          stats={landing.stats}
          activePrograms={programs.length}
          BG={BG}
          SERIF={SERIF}
          SANS={SANS}
          TEXT_MUTED={TEXT_MUTED}
          GOLD={GOLD}
          BORDER={BORDER}
        />
      )}

      {/* ── PILLARS (los escribe el seminario) ─────────────────────────── */}
      {pillars.length > 0 && <PillarsSection pillars={pillars} gold={GOLD} />}

      {/* ── PROGRAMS ───────────────────────────────────────────────────── */}
      {show.programs && path && (
        <ProgramPath path={path} basePath={basePath} gold={GOLD} goldAlpha={GOLD_ALPHA} />
      )}
      {show.programs && listedPrograms.length > 0 && (
        <ProgramsSection
          id={path ? 'otros-programas' : 'programas'}
          isOthers={!!path}
          hrefFor={programHrefFor}
          programs={listedPrograms}
          BG={BG}
          SERIF={SERIF}
          SANS={SANS}
          TEXT_PRIMARY={TEXT_PRIMARY}
          TEXT_SECONDARY={TEXT_SECONDARY}
          TEXT_MUTED={TEXT_MUTED}
          GOLD={GOLD}
          BORDER={BORDER}
        />
      )}

      {/* ── CTA ────────────────────────────────────────────────────────── */}
      {show.facultySection && (
        <FacultySection
          members={faculty}
          intro={landing.facultyIntro?.trim()}
          gold={GOLD}
          goldAlpha={GOLD_ALPHA}
        />
      )}

      {show.apply && (
        <CTASection
          tenant={tenant}
          SERIF={SERIF}
          SANS={SANS}
          TEXT_PRIMARY={TEXT_PRIMARY}
          TEXT_SECONDARY={TEXT_SECONDARY}
          BORDER={BORDER}
          onApply={handleApply}
        />
      )}

      {/* ── FOOTER ─────────────────────────────────────────────────────── */}
      <FooterSection
        tenant={tenant}
        landing={landing}
        logoUrl={logoUrl}
        SERIF={SERIF}
        SANS={SANS}
        TEXT_PRIMARY={TEXT_PRIMARY}
        TEXT_MUTED={TEXT_MUTED}
        TEXT_SECONDARY={TEXT_SECONDARY}
        GOLD={GOLD}
        BORDER={BORDER}
        show={show}
        onPortal={handlePortal}
        onFaculty={handleFaculty}
      />
    </>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
//  SUB-COMPONENTS
// ═══════════════════════════════════════════════════════════════════════════════

// ── Navbar ────────────────────────────────────────────────────────────────────

function NavBar({
  tenant,
  logoUrl,
  GOLD,
  SERIF,
  SANS,
  TEXT_PRIMARY,
  BORDER,
  scrolled,
  show,
  onApply,
  onPortal,
}: {
  tenant: TenantSnapshot;
  logoUrl: string | undefined;
  GOLD: string;
  SERIF: string;
  SANS: string;
  TEXT_PRIMARY: string;
  BORDER: string;
  scrolled: boolean;
  show: LandingVisibility;
  onApply: () => void;
  onPortal: () => void;
}) {
  const { t } = useTranslation('sis');
  const [open, setOpen] = useState(false);
  const isMobile = useIsTablet(); // hamburger at tablet breakpoint
  return (
    <>
      <nav className={`sl-nav${scrolled ? ' scrolled' : ''}`}>
        {/* Brand */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, flex: 1, minWidth: 0 }}>
          {logoUrl ? (
            <img
              src={logoUrl}
              alt={tenant.name}
              style={{ height: 30, objectFit: 'contain', flexShrink: 0 }}
            />
          ) : (
            <SchoolRounded style={{ color: GOLD, fontSize: 22, flexShrink: 0 }} />
          )}
          <span
            style={{
              fontFamily: SERIF,
              fontSize: 15,
              fontWeight: 500,
              color: TEXT_PRIMARY,
              letterSpacing: '0.01em',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {tenant.name}
          </span>
        </div>

        {/* Desktop nav */}
        <div className="nav-desktop">
          {show.programs && (
            <a className="nav-link" href="#programas" style={{ fontFamily: SANS }}>
              {t('publicLanding.nav.programs')}
            </a>
          )}
          {show.about && (
            <a className="nav-link" href="#nosotros" style={{ fontFamily: SANS }}>
              {t('publicLanding.nav.about')}
            </a>
          )}
          {show.facultySection && (
            <a className="nav-link" href="#docentes" style={{ fontFamily: SANS }}>
              {t('publicLanding.nav.faculty')}
            </a>
          )}
          {show.portal && (
            <button
              onClick={onPortal}
              className="btn-ghost"
              style={{ padding: '8px 20px', fontFamily: SANS }}
            >
              {t('publicLanding.nav.students')}
            </button>
          )}
          {show.apply && (
            <button
              onClick={onApply}
              className="btn-cta"
              style={{ padding: '8px 20px', fontFamily: SANS }}
            >
              {t('publicLanding.nav.apply')}
            </button>
          )}
        </div>

        {/* Mobile hamburger */}
        <button
          className="nav-hamburger"
          onClick={() => setOpen((o) => !o)}
          aria-label={open ? t('publicLanding.nav.closeMenu') : t('publicLanding.nav.openMenu')}
          style={{ display: isMobile ? 'flex' : 'none' }}
        >
          {open ? (
            <svg
              width="22"
              height="22"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
            >
              <line x1="18" y1="6" x2="6" y2="18" />
              <line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          ) : (
            <svg
              width="22"
              height="22"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
            >
              <line x1="3" y1="8" x2="21" y2="8" />
              <line x1="3" y1="14" x2="21" y2="14" />
            </svg>
          )}
        </button>
      </nav>

      {/* Mobile dropdown */}
      <div className={`nav-mobile-menu${open ? ' open' : ''}`}>
        {show.programs && (
          <a
            className="nav-link"
            href="#programas"
            style={{ fontFamily: SANS, fontSize: 15, padding: '4px 0' }}
            onClick={() => setOpen(false)}
          >
            {t('publicLanding.nav.programs')}
          </a>
        )}
        {show.about && (
          <a
            className="nav-link"
            href="#nosotros"
            style={{ fontFamily: SANS, fontSize: 15, padding: '4px 0' }}
            onClick={() => setOpen(false)}
          >
            {t('publicLanding.nav.about')}
          </a>
        )}
        {show.facultySection && (
          <a
            className="nav-link"
            href="#docentes"
            style={{ fontFamily: SANS, fontSize: 15, padding: '4px 0' }}
            onClick={() => setOpen(false)}
          >
            {t('publicLanding.nav.faculty')}
          </a>
        )}
        <div
          style={{
            borderTop: `1px solid ${BORDER}`,
            paddingTop: 16,
            display: 'flex',
            flexDirection: 'column',
            gap: 12,
          }}
        >
          {show.portal && (
            <button
              onClick={() => {
                onPortal();
                setOpen(false);
              }}
              className="btn-ghost full-mobile"
              style={{ fontFamily: SANS }}
            >
              {t('publicLanding.nav.students')}
            </button>
          )}
          {show.apply && (
            <button
              onClick={() => {
                onApply();
                setOpen(false);
              }}
              className="btn-cta full-mobile"
              style={{ fontFamily: SANS }}
            >
              {t('publicLanding.nav.apply')}
            </button>
          )}
        </div>
      </div>
    </>
  );
}

// ── Hero ──────────────────────────────────────────────────────────────────────

function HeroSection({
  tenant,
  landing,
  logoUrl,
  GOLD,
  SERIF,
  SANS,
  BG,
  TEXT_PRIMARY,
  TEXT_SECONDARY,
  BORDER,
  GOLD_ALPHA,
  featuredEvents,
  today,
  show,
  onApply,
  onPortal,
  onFaculty,
}: {
  tenant: TenantSnapshot;
  landing: Partial<LandingConfig>;
  logoUrl: string | undefined;
  GOLD: string;
  SERIF: string;
  SANS: string;
  BG: string;
  TEXT_PRIMARY: string;
  TEXT_SECONDARY: string;
  BORDER: string;
  GOLD_ALPHA: (a: number) => string;
  featuredEvents: FeaturedEvent[];
  today: string;
  show: LandingVisibility;
  onApply: () => void;
  onPortal: () => void;
  onFaculty: () => void;
}) {
  const heroImg = landing.heroImageUrl;
  return (
    <section
      style={{
        position: 'relative',
        minHeight: '100vh',
        background: BG,
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'flex-end',
        paddingBottom: 96,
        overflow: 'hidden',
      }}
    >
      {/* Optional background photo — diluted behind dark overlay */}
      {heroImg && (
        <div
          style={{
            position: 'absolute',
            inset: 0,
            pointerEvents: 'none',
            backgroundImage: `url(${heroImg})`,
            backgroundSize: 'cover',
            backgroundPosition: 'center 30%',
            backgroundRepeat: 'no-repeat',
            opacity: 0.18,
            filter: 'grayscale(30%) brightness(0.7)',
            transition: 'opacity 0.6s ease',
          }}
        />
      )}

      {/* Image gradient fade — keeps bottom readable when image is set */}
      {heroImg && (
        <div
          style={{
            position: 'absolute',
            inset: 0,
            pointerEvents: 'none',
            background: `linear-gradient(to bottom, ${BG}55 0%, transparent 30%, transparent 60%, ${BG} 100%)`,
          }}
        />
      )}

      {/* Subtle grid pattern — very low opacity, purely structural */}
      <div
        style={{
          position: 'absolute',
          inset: 0,
          pointerEvents: 'none',
          backgroundImage: `
                    linear-gradient(rgba(255,255,255,${heroImg ? '0.018' : '0.025'}) 1px, transparent 1px),
                    linear-gradient(90deg, rgba(255,255,255,${heroImg ? '0.018' : '0.025'}) 1px, transparent 1px)
                `,
          backgroundSize: '80px 80px',
        }}
      />

      {/* Gold vignette — bottom-center only, very subtle */}
      <div
        style={{
          position: 'absolute',
          bottom: 0,
          left: '30%',
          right: '30%',
          height: '40%',
          background: `radial-gradient(ellipse at 50% 100%, ${GOLD_ALPHA(heroImg ? 0.12 : 0.08)} 0%, transparent 70%)`,
          pointerEvents: 'none',
        }}
      />

      {/* Content */}
      <HeroContent
        tenant={tenant}
        landing={landing}
        logoUrl={logoUrl}
        GOLD={GOLD}
        SERIF={SERIF}
        SANS={SANS}
        TEXT_PRIMARY={TEXT_PRIMARY}
        TEXT_SECONDARY={TEXT_SECONDARY}
        BORDER={BORDER}
        featuredEvents={featuredEvents}
        today={today}
        show={show}
        onApply={onApply}
        onPortal={onPortal}
        onFaculty={onFaculty}
      />
    </section>
  );
}

function HeroContent({
  tenant,
  landing,
  logoUrl,
  GOLD,
  SERIF,
  SANS,
  TEXT_PRIMARY,
  TEXT_SECONDARY,
  BORDER,
  featuredEvents,
  today,
  show,
  onApply,
  onPortal,
  onFaculty,
}: {
  tenant: TenantSnapshot;
  landing: Partial<LandingConfig>;
  logoUrl: string | undefined;
  GOLD: string;
  SERIF: string;
  SANS: string;
  TEXT_PRIMARY: string;
  TEXT_SECONDARY: string;
  BORDER: string;
  featuredEvents: FeaturedEvent[];
  today: string;
  show: LandingVisibility;
  onApply: () => void;
  onPortal: () => void;
  onFaculty: () => void;
}) {
  const { t } = useTranslation('sis');
  const isMobile = useIsMobile();
  const isTablet = useIsTablet();
  // Sin eventos vigentes, la portada queda exactamente como siempre.
  const hasEvents = featuredEvents.length > 0;

  const copy = (
    <>
      {/* Institution badge — solo sin logo: con logo, la barra de arriba ya
          muestra la identidad y repetirla aquí recarga la portada. */}
      {!logoUrl && (
        <div
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 8,
            borderLeft: `2px solid ${GOLD}`,
            paddingLeft: 12,
            marginBottom: 32,
            animation: 'fadeUp 0.8s ease both',
          }}
        >
          <SchoolRounded style={{ color: GOLD, fontSize: 18 }} />
          <span
            style={{
              fontFamily: SANS,
              fontSize: 11,
              fontWeight: 600,
              letterSpacing: '0.18em',
              textTransform: 'uppercase',
              color: GOLD,
            }}
          >
            {tenant.slug?.toUpperCase()}
          </span>
        </div>
      )}

      {/* Title */}
      <h1
        className="hero-title"
        style={{
          fontFamily: SERIF,
          fontSize: hasEvents ? 'clamp(2.6rem, 6vw, 5.4rem)' : 'clamp(2.6rem, 8vw, 6.5rem)',
          fontWeight: 500,
          lineHeight: 1.04,
          letterSpacing: '-0.01em',
          color: TEXT_PRIMARY,
          marginBottom: landing.heroSubtitle ? 12 : 24,
          animation: 'fadeUp 0.9s 0.1s ease both',
        }}
      >
        {landing.heroTitle || tenant.name}
      </h1>

      {/* Custom subtitle/tagline when configured */}
      {landing.heroSubtitle && (
        <p
          style={{
            fontFamily: SERIF,
            fontSize: isMobile ? 18 : 22,
            lineHeight: 1.4,
            color: GOLD,
            marginBottom: 20,
            fontStyle: 'italic',
            fontWeight: 400,
            animation: 'fadeUp 0.9s 0.15s ease both',
          }}
        >
          {landing.heroSubtitle}
        </p>
      )}

      {/* Tagline */}
      <p
        style={{
          fontFamily: SANS,
          fontSize: isMobile ? 15 : 17,
          lineHeight: 1.65,
          color: TEXT_SECONDARY,
          maxWidth: 520,
          marginBottom: 44,
          animation: 'fadeUp 0.9s 0.2s ease both',
        }}
      >
        {landing.about ? leadSentence(landing.about) : t('publicLanding.hero.defaultTagline')}
      </p>

      {/* CTAs */}
      <div
        style={{
          display: 'flex',
          gap: 12,
          flexWrap: 'wrap',
          flexDirection: isMobile ? 'column' : 'row',
          animation: 'fadeUp 0.9s 0.3s ease both',
        }}
      >
        {show.apply && (
          <button onClick={onApply} className={`btn-cta${isMobile ? ' full-mobile' : ''}`}>
            <PersonAddRounded style={{ fontSize: 17 }} />
            {t('publicLanding.hero.apply')}
            <ArrowForwardRounded style={{ fontSize: 16 }} />
          </button>
        )}
        {show.portal && (
          <button onClick={onPortal} className={`btn-ghost${isMobile ? ' full-mobile' : ''}`}>
            <MenuBookRounded style={{ fontSize: 17 }} />
            {t('publicLanding.hero.portal')}
          </button>
        )}
        {show.faculty && (
          <button onClick={onFaculty} className={`btn-ghost${isMobile ? ' full-mobile' : ''}`}>
            <LoginRounded style={{ fontSize: 17 }} />
            {t('publicLanding.hero.faculty')}
          </button>
        )}
      </div>
    </>
  );

  return (
    <>
      {hasEvents ? (
        // Portada a la izquierda y eventos a la derecha; en tablet y móvil, los
        // eventos bajan bajo los botones.
        <div
          style={{
            position: 'relative',
            display: 'grid',
            gridTemplateColumns: isTablet
              ? 'minmax(0, 1fr)'
              : 'minmax(0, 1fr) minmax(360px, 420px)',
            gap: isMobile ? 36 : isTablet ? 48 : 64,
            alignItems: 'end',
            paddingTop: isMobile ? 100 : 120,
            paddingLeft: isMobile ? 24 : 80,
            paddingRight: isMobile ? 24 : 80,
          }}
        >
          <div style={{ minWidth: 0 }}>{copy}</div>
          <div style={{ minWidth: 0, maxWidth: isTablet && !isMobile ? 460 : undefined }}>
            <FeaturedEvents events={featuredEvents} today={today} serif={SERIF} sans={SANS} />
          </div>
        </div>
      ) : (
        <div
          style={{
            position: 'relative',
            maxWidth: 900,
            marginLeft: isMobile ? 0 : 80,
            paddingTop: 120,
            paddingLeft: isMobile ? 24 : 0,
            paddingRight: isMobile ? 24 : 0,
          }}
        >
          {copy}
        </div>
      )}

      {/* Scroll indicator — hide on mobile, and when events take the right side */}
      {!isMobile && !hasEvents && (
        <div
          style={{
            position: 'absolute',
            bottom: 36,
            right: 56,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: 10,
            animation: 'fadeUp 1.2s 0.6s ease both',
          }}
        >
          <span className="vertical-label">{t('publicLanding.hero.discover')}</span>
          <div
            style={{
              width: 1,
              height: 48,
              background: `linear-gradient(to bottom, ${BORDER}, transparent)`,
            }}
          />
        </div>
      )}
    </>
  );
}

// ── Mission ───────────────────────────────────────────────────────────────────

function MissionSection({
  landing,
  config,
  BG2,
  SERIF,
  SANS,
  TEXT_PRIMARY,
  TEXT_SECONDARY,
  GOLD,
  BORDER,
}: {
  landing: Partial<LandingConfig>;
  config: TenantConfig | undefined;
  BG2: string;
  SERIF: string;
  SANS: string;
  TEXT_PRIMARY: string;
  TEXT_SECONDARY: string;
  GOLD: string;
  BORDER: string;
}) {
  const { t } = useTranslation('sis');
  const { ref, visible } = useReveal();
  const [confessionOpen, setConfessionOpen] = useState(false);
  const isMobile = useIsTablet();

  // Solo lo que el seminario escribió: la misión (Marco institucional) y su
  // «Acerca» (Landing). Nada de afirmaciones escritas en el código, que antes
  // salían iguales en el landing de todos los seminarios.
  const missionText = config?.mission?.text?.trim();
  const scripture = config?.mission?.scripture?.trim();
  const about = landing.about?.trim();
  const statement = missionText || about;
  const aside = missionText ? about : undefined;
  const hasConfession = hasRichContent(config?.doctrinalStatement);
  const twoColumns = !isMobile && !!(aside || hasConfession);

  return (
    <section id="nosotros" style={{ background: BG2, borderTop: `1px solid ${BORDER}` }}>
      <div
        style={{
          maxWidth: 1200,
          margin: '0 auto',
          padding: isMobile ? '64px 24px' : '100px 64px',
          display: 'grid',
          gridTemplateColumns: twoColumns ? '1.15fr 1fr' : '1fr',
          gap: isMobile ? 36 : 80,
          alignItems: 'start',
        }}
      >
        <div ref={ref} className={`reveal${visible ? ' visible' : ''}`}>
          <p className="section-label">
            {missionText ? t('publicLanding.about.missionLabel') : t('publicLanding.about.label')}
          </p>
          <p
            style={
              {
                fontFamily: SERIF,
                fontSize: 'clamp(1.35rem, 2.4vw, 1.9rem)',
                fontWeight: 400,
                lineHeight: 1.5,
                color: TEXT_PRIMARY,
                borderLeft: `2px solid ${GOLD}`,
                paddingLeft: 28,
                margin: 0,
                maxWidth: 640,
                textWrap: 'pretty',
              } as CSSProperties
            }
          >
            {statement}
          </p>
          {missionText && scripture && (
            <p
              style={{
                marginTop: 18,
                paddingLeft: 30,
                fontFamily: SANS,
                fontSize: 12,
                fontWeight: 600,
                letterSpacing: '0.16em',
                textTransform: 'uppercase',
                color: GOLD,
              }}
            >
              {scripture}
            </p>
          )}
        </div>

        {(aside || hasConfession) && (
          <div
            className={`reveal reveal-delay-2${visible ? ' visible' : ''}`}
            style={{ display: 'flex', flexDirection: 'column', gap: 28 }}
          >
            {aside && (
              <>
                <hr style={{ border: 'none', borderTop: `1px solid ${BORDER}`, margin: 0 }} />
                <p
                  style={{ fontFamily: SANS, fontSize: 15, lineHeight: 1.8, color: TEXT_SECONDARY }}
                >
                  {aside}
                </p>
              </>
            )}
            {hasConfession && (
              <>
                <hr style={{ border: 'none', borderTop: `1px solid ${BORDER}`, margin: 0 }} />
                <button
                  type="button"
                  className="btn-ghost"
                  style={{ alignSelf: 'flex-start', fontFamily: SANS }}
                  onClick={() => setConfessionOpen(true)}
                >
                  <MenuBookOutlined style={{ fontSize: 17 }} />
                  {t('publicLanding.about.readConfession')}
                </button>
              </>
            )}
          </div>
        )}
      </div>
      {confessionOpen && config?.doctrinalStatement && (
        <Suspense fallback={null}>
          <ConfessionDialog
            open
            onClose={() => setConfessionOpen(false)}
            value={config.doctrinalStatement}
            serif={SERIF}
          />
        </Suspense>
      )}
    </section>
  );
}

/** Un documento TipTap con al menos un nodo de texto no vacío. */
function hasRichContent(doc: Record<string, unknown> | undefined): boolean {
  if (!doc) return false;
  const walk = (node: unknown): boolean => {
    if (!node || typeof node !== 'object') return false;
    const n = node as { text?: unknown; content?: unknown };
    if (typeof n.text === 'string' && n.text.trim()) return true;
    return Array.isArray(n.content) && n.content.some(walk);
  };
  return walk(doc);
}

// ── Stats ────────────────────────────────────────────────────────────────────

function StatsSection({
  stats,
  activePrograms,
  BG,
  SERIF,
  SANS,
  TEXT_MUTED,
  GOLD,
  BORDER,
}: {
  stats: StatItem[];
  activePrograms: number;
  BG: string;
  SERIF: string;
  SANS: string;
  TEXT_MUTED: string;
  GOLD: string;
  BORDER: string;
}) {
  const { ref, visible } = useReveal();
  const isMobile = useIsMobile();
  // Una cifra «automática» no se escribe: sale del sistema.
  const values = stats.map((s) => ({
    ...s,
    value: s.source === 'activePrograms' ? activePrograms : s.value,
  }));
  // En móvil, 3 cifras van en una sola fila (con 2 columnas, la tercera quedaba sola).
  const cols = values.length <= 3 ? values.length : isMobile ? 2 : 4;
  const compact = isMobile && values.length === 3;
  return (
    <section style={{ background: BG, borderTop: `1px solid ${BORDER}` }}>
      <div
        ref={ref}
        className="stat-grid"
        style={{
          maxWidth: 1200,
          margin: '0 auto',
          display: 'grid',
          gridTemplateColumns: `repeat(${cols}, minmax(0, 1fr))`,
        }}
      >
        {values.map((s, i) => (
          <div
            key={i}
            className={`reveal reveal-delay-${Math.min(i + 1, 4)}${visible ? ' visible' : ''}`}
            style={{
              padding: compact ? '32px 8px' : isMobile ? '36px 24px' : '56px 48px',
              borderRight: (i + 1) % cols !== 0 ? `1px solid ${BORDER}` : 'none',
              textAlign: 'center',
            }}
          >
            <div
              style={{
                fontFamily: SERIF,
                fontSize: compact ? '2.2rem' : 'clamp(2.5rem, 5vw, 4rem)',
                fontWeight: 500,
                color: GOLD,
                lineHeight: 1,
                marginBottom: 10,
                fontVariantNumeric: 'lining-nums',
              }}
            >
              <AnimatedCounter target={s.value} suffix={s.suffix} />
            </div>
            <p
              style={{
                fontFamily: SANS,
                fontSize: compact ? 10.5 : 12,
                letterSpacing: '0.1em',
                textTransform: 'uppercase',
                color: TEXT_MUTED,
              }}
            >
              {s.label}
            </p>
          </div>
        ))}
      </div>
    </section>
  );
}

// ── Programs ──────────────────────────────────────────────────────────────────

function ProgramsSection({
  id,
  isOthers,
  hrefFor,
  programs,
  BG,
  SERIF,
  SANS,
  TEXT_PRIMARY,
  TEXT_SECONDARY,
  TEXT_MUTED,
  GOLD,
  BORDER,
}: {
  id: string;
  isOthers: boolean;
  hrefFor: (p: Program) => string | undefined;
  programs: Program[];
  BG: string;
  SERIF: string;
  SANS: string;
  TEXT_PRIMARY: string;
  TEXT_SECONDARY: string;
  TEXT_MUTED: string;
  GOLD: string;
  BORDER: string;
}) {
  const { t } = useTranslation('sis');
  const { ref, visible } = useReveal();

  const isMobile = useIsTablet();
  return (
    <section id={id} style={{ background: BG }}>
      <div
        style={{ maxWidth: 1200, margin: '0 auto', padding: isMobile ? '56px 24px' : '96px 64px' }}
      >
        {/* Header row */}
        <div
          ref={ref}
          style={{
            display: 'flex',
            flexDirection: isMobile ? 'column' : 'row',
            justifyContent: 'space-between',
            alignItems: isMobile ? 'flex-start' : 'flex-end',
            gap: 12,
            marginBottom: 40,
            borderBottom: `1px solid ${BORDER}`,
            paddingBottom: 24,
          }}
        >
          <div>
            <p className={`section-label reveal${visible ? ' visible' : ''}`}>
              {t('publicLanding.programs.label')}
            </p>
            <h2
              className={`reveal reveal-delay-1${visible ? ' visible' : ''}`}
              style={{
                fontFamily: SERIF,
                fontSize: 'clamp(2rem, 4vw, 3rem)',
                fontWeight: 400,
                color: TEXT_PRIMARY,
                lineHeight: 1.15,
              }}
            >
              {isOthers ? t('publicLanding.path.others') : t('publicLanding.programs.title')}
            </h2>
          </div>
          <span
            className={`reveal reveal-delay-2${visible ? ' visible' : ''}`}
            style={{
              fontFamily: SANS,
              fontSize: 13,
              color: TEXT_MUTED,
              letterSpacing: '0.04em',
            }}
          >
            {t('publicLanding.programs.count', { count: programs.length })}
          </span>
        </div>

        {/* Programs list — editorial rows, not cards */}
        <div style={{ borderTop: `1px solid ${BORDER}` }}>
          {programs.map((p, i) => (
            <ProgramRow
              key={p.id}
              program={p}
              href={hrefFor(p)}
              index={i}
              SERIF={SERIF}
              SANS={SANS}
              TEXT_PRIMARY={TEXT_PRIMARY}
              TEXT_SECONDARY={TEXT_SECONDARY}
              TEXT_MUTED={TEXT_MUTED}
              GOLD={GOLD}
              BORDER={BORDER}
            />
          ))}
        </div>
      </div>
    </section>
  );
}

function ProgramRow({
  program,
  href,
  index,
  SERIF,
  SANS,
  TEXT_PRIMARY,
  TEXT_SECONDARY,
  TEXT_MUTED,
  GOLD,
  BORDER,
}: {
  program: Program;
  href: string | undefined;
  index: number;
  SERIF: string;
  SANS: string;
  TEXT_PRIMARY: string;
  TEXT_SECONDARY: string;
  TEXT_MUTED: string;
  GOLD: string;
  BORDER: string;
}) {
  const { ref, visible } = useReveal();
  const [hovered, setHovered] = useState(false);
  const navigate = useNavigate();

  const isMobile = useIsMobile();
  // Con página pública, la fila entera lleva al programa.
  const linkProps = href
    ? {
        role: 'link' as const,
        tabIndex: 0,
        onClick: () => navigate(href),
        onKeyDown: (e: { key: string }) => {
          if (e.key === 'Enter') navigate(href);
        },
      }
    : {};
  return (
    <div
      ref={ref}
      className={`reveal reveal-delay-${Math.min(index + 1, 4)}${visible ? ' visible' : ''}`}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      {...linkProps}
      style={{
        display: 'grid',
        gridTemplateColumns: isMobile ? '32px 1fr' : '48px 1fr auto',
        gap: isMobile ? 16 : 24,
        alignItems: 'center',
        padding: isMobile ? '20px 0' : '28px 0',
        borderBottom: `1px solid ${BORDER}`,
        transition: 'background 0.2s',
        cursor: href ? 'pointer' : 'default',
      }}
    >
      {/* Index number */}
      <span style={{ fontFamily: SANS, fontSize: 12, color: TEXT_MUTED, letterSpacing: '0.08em' }}>
        {String(index + 1).padStart(2, '0')}
      </span>

      {/* Name + description */}
      <div>
        <h3
          style={{
            fontFamily: SERIF,
            fontSize: 22,
            fontWeight: 500,
            color: hovered ? GOLD : TEXT_PRIMARY,
            transition: 'color 0.2s',
            marginBottom: 4,
          }}
        >
          {program.name}
        </h3>
        {program.description && (
          <p style={{ fontFamily: SANS, fontSize: 13, color: TEXT_SECONDARY, lineHeight: 1.6 }}>
            {leadSentence(program.description, 140)}
          </p>
        )}
      </div>

      {/* Code — hidden on mobile to keep grid clean */}
      {!isMobile && (
        <div style={{ textAlign: 'right', flexShrink: 0 }}>
          {program.code && (
            <span
              style={{
                fontFamily: SANS,
                fontSize: 11,
                letterSpacing: '0.12em',
                textTransform: 'uppercase',
                color: TEXT_MUTED,
                display: 'block',
                marginBottom: 4,
              }}
            >
              {program.code}
            </span>
          )}
        </div>
      )}
    </div>
  );
}

// ── CTA ───────────────────────────────────────────────────────────────────────

function CTASection({
  tenant,
  SERIF,
  SANS,
  TEXT_PRIMARY,
  TEXT_SECONDARY,
  BORDER,
  onApply,
}: {
  tenant: TenantSnapshot;
  SERIF: string;
  SANS: string;
  TEXT_PRIMARY: string;
  TEXT_SECONDARY: string;
  BORDER: string;
  onApply: () => void;
}) {
  const { t } = useTranslation('sis');
  const { ref, visible } = useReveal();

  const isMobile = useIsTablet();
  return (
    <section
      style={{
        background: '#0a0c0c',
        borderTop: `1px solid ${BORDER}`,
        borderBottom: `1px solid ${BORDER}`,
      }}
    >
      <div
        ref={ref}
        style={{
          maxWidth: 920,
          margin: '0 auto',
          padding: isMobile ? '72px 24px' : '120px 64px',
          textAlign: 'center',
        }}
      >
        {/* «Admisión», no «convocatoria abierta»: el sistema no sabe si hay una. */}
        <p
          className={`section-label reveal${visible ? ' visible' : ''}`}
          style={{ marginBottom: 24 }}
        >
          {t('publicLanding.cta.label')}
        </p>
        <h2
          className={`reveal reveal-delay-1${visible ? ' visible' : ''}`}
          style={
            {
              fontFamily: SERIF,
              fontSize: 'clamp(2.2rem, 5vw, 4rem)',
              fontWeight: 400,
              lineHeight: 1.15,
              color: TEXT_PRIMARY,
              marginBottom: 20,
              textWrap: 'balance',
            } as CSSProperties
          }
        >
          {t('publicLanding.cta.title', { name: tenant.name })}
        </h2>
        <p
          className={`reveal reveal-delay-2${visible ? ' visible' : ''}`}
          style={{
            fontFamily: SANS,
            fontSize: 16,
            lineHeight: 1.75,
            color: TEXT_SECONDARY,
            maxWidth: 560,
            margin: '0 auto 48px',
          }}
        >
          {t('publicLanding.cta.body')}
        </p>
        <div
          className={`reveal reveal-delay-3${visible ? ' visible' : ''}`}
          style={{ display: 'flex', gap: 16, justifyContent: 'center', flexWrap: 'wrap' }}
        >
          <button
            onClick={onApply}
            className="btn-cta"
            style={{ padding: '16px 40px', fontSize: 14 }}
          >
            <PersonAddRounded style={{ fontSize: 18 }} />
            {t('publicLanding.cta.button')}
            <ArrowForwardRounded style={{ fontSize: 16 }} />
          </button>
        </div>
      </div>
    </section>
  );
}

// ── Footer ────────────────────────────────────────────────────────────────────

function FooterSection({
  tenant,
  landing,
  logoUrl,
  SERIF,
  SANS,
  TEXT_PRIMARY,
  TEXT_MUTED,
  TEXT_SECONDARY,
  GOLD,
  BORDER,
  show,
  onPortal,
  onFaculty,
}: {
  tenant: TenantSnapshot;
  landing: Partial<LandingConfig>;
  logoUrl: string | undefined;
  SERIF: string;
  SANS: string;
  TEXT_PRIMARY: string;
  TEXT_MUTED: string;
  TEXT_SECONDARY: string;
  GOLD: string;
  BORDER: string;
  show: LandingVisibility;
  onPortal: () => void;
  onFaculty: () => void;
}) {
  const { t } = useTranslation('sis');
  const isMobile = useIsTablet();
  const accesses = [
    ...(show.portal ? [{ label: t('publicLanding.footer.portal'), action: onPortal }] : []),
    ...(show.faculty ? [{ label: t('publicLanding.footer.faculty'), action: onFaculty }] : []),
  ];
  const email = landing.contactEmail?.trim();
  const showContact = !!email || hasContact(landing.contact);
  const columns = 1 + (accesses.length > 0 ? 1 : 0) + (showContact ? 1 : 0);
  return (
    <footer style={{ background: '#080a0a', borderTop: `1px solid ${BORDER}` }}>
      <div
        style={{
          maxWidth: 1200,
          margin: '0 auto',
          padding: isMobile ? '48px 24px 32px' : '72px 64px 40px',
        }}
      >
        {/* Top row */}
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: isMobile ? '1fr' : ['1fr', '2fr 1fr', '2fr 1fr 1fr'][columns - 1],
            gap: isMobile ? 40 : 64,
            paddingBottom: 48,
            borderBottom: `1px solid ${BORDER}`,
            marginBottom: 28,
          }}
        >
          {/* Brand */}
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 16 }}>
              {logoUrl ? (
                <img src={logoUrl} alt={tenant.name} style={{ height: 28, objectFit: 'contain' }} />
              ) : (
                <SchoolRounded style={{ color: GOLD, fontSize: 22 }} />
              )}
              <span
                style={{ fontFamily: SERIF, fontSize: 18, fontWeight: 500, color: TEXT_PRIMARY }}
              >
                {tenant.name}
              </span>
            </div>
            {landing.heroSubtitle?.trim() && (
              <p
                style={{
                  fontFamily: SANS,
                  fontSize: 14,
                  lineHeight: 1.7,
                  color: TEXT_MUTED,
                  maxWidth: 300,
                }}
              >
                {landing.heroSubtitle.trim()}
              </p>
            )}
          </div>

          {/* Accesos */}
          {accesses.length > 0 && (
            <div>
              <p
                style={{
                  fontFamily: SANS,
                  fontSize: 11,
                  letterSpacing: '0.18em',
                  textTransform: 'uppercase',
                  color: GOLD,
                  marginBottom: 20,
                }}
              >
                {t('publicLanding.footer.accesses')}
              </p>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                {accesses.map(({ label, action }) => (
                  <button
                    key={label}
                    onClick={action}
                    style={{
                      background: 'none',
                      border: 'none',
                      cursor: 'pointer',
                      padding: 0,
                      fontFamily: SANS,
                      fontSize: 14,
                      color: TEXT_SECONDARY,
                      textAlign: 'left',
                      transition: 'color 0.2s',
                    }}
                    onMouseEnter={(e) => (e.currentTarget.style.color = TEXT_PRIMARY)}
                    onMouseLeave={(e) => (e.currentTarget.style.color = TEXT_SECONDARY)}
                  >
                    {label}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Contacto */}
          {showContact && (
            <div>
              <p
                style={{
                  fontFamily: SANS,
                  fontSize: 11,
                  letterSpacing: '0.18em',
                  textTransform: 'uppercase',
                  color: GOLD,
                  marginBottom: 20,
                }}
              >
                {t('publicLanding.footer.contact')}
              </p>
              <ContactBlock email={email} contact={landing.contact} />
            </div>
          )}
        </div>

        {/* Bottom row */}
        <div
          style={{
            display: 'flex',
            flexDirection: isMobile ? 'column' : 'row',
            gap: isMobile ? 8 : 0,
            justifyContent: 'space-between',
            alignItems: isMobile ? 'flex-start' : 'center',
          }}
        >
          <p style={{ fontFamily: SANS, fontSize: 12, color: TEXT_MUTED }}>
            {t('publicLanding.footer.rights', {
              year: new Date().getFullYear(),
              name: tenant.name,
            })}
          </p>
          <p style={{ fontFamily: SANS, fontSize: 11, color: TEXT_MUTED, letterSpacing: '0.06em' }}>
            {t('publicLanding.footer.poweredBy')}{' '}
            <span style={{ color: TEXT_SECONDARY }}>Dosfilos Seminary SIS</span>
          </p>
        </div>
      </div>
    </footer>
  );
}
